import {Injectable} from '@angular/core';
import {DatePipe, LocationStrategy} from '@angular/common';
import {AlertMessage, AlertService} from '../alert.service';
import {LoggerService} from '../logger.service';
import {ConfigResponse, PollInfoRecord} from '../../models';
import {MAP} from '../../index';

// see hbs.poll.constants.type.AnonymityType: "Not Anonymous", "Anonymous to poll takers only", "Anonymous"

export const AnonymityDesc = {
    takerUser: [
        'Your name will be recorded with your responses and will be visible to all with access to the poll\'s results.',
        'The poll\'s author(s) will see your name attached to the responses you make.',
        'Your responses to this poll will be anonymous.'
    ],
    // show nothing to anonymous taker
    takerAnonymous: [
        '',
        '',
        ''
    ],
    author: [
        'Everyone can see poll takers’ responses. Responses include names',
        'Only poll author can see poll takers’ responses. Responses include names',
        'Responses are anonymous even to poll authors'
    ]

};

export const ResultsAvailabilityDesc = [
    'Poll results are available to poll takers now.',
    'Poll results will be available to poll takers for this poll after @@.',
    'Poll results will be available to poll takers for this poll after it closes on @@.',
    'Poll results may be available to poll takers for this poll after it closes.' +
    ' However, no close date has been defined for this poll.',
    'This poll\'s results will not be available to poll takers.'
];

export class ResultsAvailability {
    constructor(public availabilityDesc: string,   // availability description including date and time
                public availabilityDate: Date,     // availability date
                public availableNow: boolean,      // whether poll is availabile now in general (taker state is not counted)
                public takerCompleted: boolean) {  // whether taker completed poll (taker state is counted)
    }
}

export class UserRelatedData {
    constructor(public isSignedUser: boolean, public loginUrl: string, public logoutUrl: string) {
        ;
    }
}

@Injectable()
export class PollUtilService {

    private datePipe = new DatePipe('EN');

    // see hbs.poll.constants.type.AnonymityType
    private TAKER_USER_ANONYMITY_DESC: any;
    private TAKER_ANONYMOUS_ANONYMITY_DESC: any;
    private RESULTS_AVAILABILITY_DESC: any;

    constructor(private alertService: AlertService,
                protected locationStrategy: LocationStrategy,
                private logger: LoggerService) {

        this.TAKER_USER_ANONYMITY_DESC = {};
        AnonymityDesc.takerUser.reduce((acc, str) => {
            this.TAKER_USER_ANONYMITY_DESC[acc++] = str;
            return acc;
        }, 0);

        this.TAKER_ANONYMOUS_ANONYMITY_DESC = {};
        AnonymityDesc.takerAnonymous.reduce((acc, str) => {
            this.TAKER_ANONYMOUS_ANONYMITY_DESC[acc++] = str;
            return acc;
        }, 0);

        this.RESULTS_AVAILABILITY_DESC = {};
        ResultsAvailabilityDesc.reduce((acc, str) => {
            this.RESULTS_AVAILABILITY_DESC[acc++] = str;
            return acc;
        }, 0);

    }

    getTakerAnonymity(pollRecord: PollInfoRecord, anonymousUser: boolean): string {
        if (anonymousUser) {
            return this.TAKER_ANONYMOUS_ANONYMITY_DESC[pollRecord.anonymity];
        }
        return this.TAKER_USER_ANONYMITY_DESC[pollRecord.anonymity];
    }

    /**
     *
     * @param poll
     * @returns [availabilityDesc, availabilityDate, availableNow, takerCompleted]
     */
    getResultsAvailability(pollRecord: PollInfoRecord, state: string): ResultsAvailability {

        const now: Date = new Date();// TODO: current time from server or local?
        const resultsTimeStr: string = pollRecord.resultsDate;
        const resultsTime: Date = resultsTimeStr ? new Date(Date.parse(resultsTimeStr)) : null;
        const resultsTimeSet: boolean = (resultsTime !== null && resultsTime.getTime() !== 0);
        const closeTimeStr: string = pollRecord.closeDate;
        const closeTime: Date = closeTimeStr ? new Date(Date.parse(closeTimeStr)) : null;
        const closeTimeSet: boolean = (closeTime !== null && closeTime.getTime() !== 0);

        let resultsAvailabilityDescNum: number = null;
        let resultsAvailableNow = false;
        let resultsAvailabilityDate: Date = null;
        // Note: 'author' rendering does not provide pollResponse.status.state
        let takerCompleted = state === 'FINISHED';

        if (pollRecord.resultsAvailableFlag) {
            // results are/will be available online to poll takers
            if ((resultsTimeSet && now.getTime() > resultsTime.getTime()) ||
                (!resultsTimeSet && closeTimeSet && now.getTime() > closeTime.getTime())) {

                resultsAvailableNow = true;
                resultsAvailabilityDescNum = 0;
                resultsAvailabilityDate = resultsTimeSet ? resultsTime : closeTime;

            } else if (resultsTimeSet) {

                // a separate time was specified for results availability
                resultsAvailabilityDate = resultsTime;
                resultsAvailabilityDescNum = 1;

            } else {

                // results will just be available whenever the poll closes
                if (closeTimeSet) {
                    resultsAvailabilityDate = closeTime;
                    resultsAvailabilityDescNum = 2;
                } else {
                    resultsAvailabilityDescNum = 3;
                }

            }
        } else {
            resultsAvailabilityDescNum = 4;
        }

        let resultsAvailabilityDesc: string = null;

        if (resultsAvailabilityDescNum !== null) {
            resultsAvailabilityDesc = this.RESULTS_AVAILABILITY_DESC[resultsAvailabilityDescNum];
            const dateTime1 = this.datePipe.transform(resultsAvailabilityDate);
            const dateTime2 = this.datePipe.transform(resultsAvailabilityDate, 'shortTime');
            resultsAvailabilityDesc = resultsAvailabilityDesc.replace('@@', dateTime1 + ' at ' + dateTime2);
        }

        return new ResultsAvailability(resultsAvailabilityDesc, resultsAvailabilityDate, resultsAvailableNow, takerCompleted);
    }

    /**
     * Whether user is signed in
     * @param configResponse
     * @returns {boolean}
     */
    isSignedUser(configResponse: ConfigResponse): boolean {
        return configResponse && configResponse.userInfo &&
            configResponse.userInfo.firstName &&
            configResponse.userInfo.firstName != 'Guest' &&
            configResponse.userInfo.lastName != 'User';
    }

    /**
     * Provides useful data based on user info
     * @param configResponse
     * @returns {UserRelatedData}
     */
    calcUserRelatedData(configResponse: ConfigResponse): UserRelatedData {

        const isSignedUser = this.isSignedUser(configResponse);
        const loginUrl = configResponse ? configResponse.config['LOGINURL'] : null;
        const logoutUrl = configResponse ? configResponse.config['LOGOUTURL'] : null;

        return new UserRelatedData(isSignedUser, loginUrl, logoutUrl);
    }

    getPollUrl(pollId: number, scriptId: string, anonymous: boolean): string {
        const uiAnonymousPath = anonymous ? 'open/' : '';
        const uiScriptId = Number(scriptId) > 0 ? (scriptId + ',') : '';
        return '/taker/' + uiAnonymousPath + uiScriptId + pollId;
    }

    getPollAbsoluteUrl(pollId: number, scriptId: string, anonymous: boolean): string {
        const uiAnonymousPath = anonymous ? 'open/' : '';
        const uiScriptId = Number(scriptId) > 0 ? (scriptId + ',') : '';
        return this.locationStrategy.getBaseHref() + 'taker/' + uiAnonymousPath + uiScriptId + pollId;
    }

    getPollResultsUrl(pollId: number, scriptId: string, anonymous: boolean): string {
        const uiAnonymousPath = anonymous ? 'open/' : '';
        const uiScriptId = Number(scriptId) > 0 ? (scriptId + ',') : '';
        return '/taker/' + uiAnonymousPath + 'results/' + uiScriptId + pollId;
    }

    /**
     * Retrieve 'poll' parameter, parse as number; produce alert if not number
     * @param params
     * @returns {number} or null if 'poll' is missing or blank
     */
    retrievePollId(params: any): number {

        let pollId: number = null;
        const pollStr: string = params['poll'] || '';
        if (pollStr.trim().length !== 0) {
            pollId = Number(params['poll']);
            if (isNaN(pollId)) {
                this.alertService.emit(new AlertMessage('incorrect pollId parameter "' + pollStr + '"', 'error'));
                pollId = null;
            }
        }
        return pollId;

    }

    /**
     *
     * @param {MAP[]} addGroups  - groups list to add
     * @param {MAP[]} toGroups   - add to toGroups list
     * @returns {any}  - records to submit to API
     */
    makeUpdateGroups(addGroups: MAP[], toGroups: MAP[]) : any {
        const groupSet = new Set();

        const newGroups = {
            records: [].concat(toGroups, addGroups)
                .filter(group => {
                    if (groupSet.has(group.groupId)) {
                        return false;
                    }
                    groupSet.add(group.groupId);

                    return true;
                })
                .map(group => group.groupId)
        };
        return newGroups;
    }
}
